import {
  FilterSelectedTags,
  InstitutionCard,
  LoadingListCard,
} from "Explore/Components";
import { searchInstitutions } from "Explore/Services/Institution";
import { Empty } from "antd";
import { useEffect, useRef, useState, useCallback } from "react";
import { FiltersInstitutionMobile } from "./Components";
import styles from "./InstitutionList.module.scss";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import FiltersInstitution from "./Components/FiltersInstitution";
import useDeviceCheck from "Hooks/useDeviceCheck";
import { Grid, Button } from "@mui/material";
import { BookmarkPlus } from "lucide-react";
import SaveResultsModal from "Explore/Pages/MyLibrary/Components/SavedSearchResultsPanel/SaveResultsModal";
import CallToLogin from "Explore/Components/CallToLogin";
import classNames from "classnames";
import FeatureEntities from "Explore/Components/FeatureEntities";
import useAuth from "Hooks/useAuth";
import LoginRequiredSection from "Explore/Components/LoginRequired";
import { useParams } from "react-router-dom";
import { ParamsFilterKey } from "Config/filterSearchParams";
import { Helmet } from "react-helmet-async";
import InfinityPagination from "Explore/Components/Pagination/InfinityPagination";
import useInfinityPagination from "Hooks/useInfinityPagination";

const InstitutionList = () => {
  const { userTypeConfig } = useAuth({ entityType: "INSTITUTION" });
  const { filter, pagination, updateSearchParams, getPageHref } =
    useFilterAndPagination();
  const [showLoginSection, setShowLoginSection] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const handleFetchTopInstitutions = useCallback(
    async (targetLimit) => {
      const res = await searchInstitutions({
        ...filter,
        page: 0,
        rowsPerPage: targetLimit,
        numPages: 1,
      });
      return Array.isArray(res) ? res : res?.results || [];
    },
    [filter]
  );
  const { isSmallDesktop, isTablet } = useDeviceCheck();
  const scrollToRef = useRef(null);
  const { keyword } = useParams();
  const titleKeyword =
    filter?.[ParamsFilterKey.KEYWORD_QUERY]?.[0] ||
    filter?.[ParamsFilterKey.QUERY]?.[0] ||
    keyword;

  const {
    data: listData,
    currentMaxPage,
    hasNextPage,
    isLoading,
  } = useInfinityPagination({
    fetchFunction: searchInstitutions,
    page: pagination.page,
    filter,
    rowsPerPage: pagination.rowsPerPage,
    setPagination: updateSearchParams,
    errCallback: (e) => {
      if (e?.response?.status === 401) {
        setShowLoginSection(true);
      }
    },
  });

  const pageToCompute = pagination.page - 1;

  const numPagesPerFetch = userTypeConfig?.limit?.numPagesPerFetch || 1;

  useEffect(() => {
    if (keyword) {
      updateSearchParams({
        [ParamsFilterKey.QUERY]: [keyword],
      });
    }
  }, [keyword]);

  useEffect(() => {
    scrollToRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [pagination.page]);

  const currentUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;

  const generateTitle = () => {
    // Advanced title logic (commented out for future version)

    if (!titleKeyword)
      return "Top Universities & Research Institutions | Academic Online";
    const suffix = " | Academic Online";
    const maxLength = 65;

    let titleFull = `Top Institutions in ${titleKeyword}`;
    if ((titleFull + suffix).length <= maxLength) return titleFull + suffix;

    let titleShort = `${titleKeyword} Institutions`;
    if ((titleShort + suffix).length <= maxLength) return titleShort + suffix;

    return `${titleKeyword.substring(0, 40)}...${suffix}`;

    // Original simple logic
    // return "Explore Top Universities & Colleges | Academic Explorer";
  };

  const generateDescription = () => {
    return titleKeyword
      ? `Find and explore the top universities and research institutions related to ${titleKeyword}.`
      : "Search and discover top universities and research institutions worldwide. Find information about academic programs, research facilities, and more.";
  };

  return (
    <div className={styles["institution-list"]}>
      <Helmet>
        <title>{generateTitle()}</title>
        <meta name="description" content={generateDescription()} />
        <link rel="canonical" href={currentUrl} />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content={generateTitle()} />
        <meta property="og:description" content={generateDescription()} />
      </Helmet>
      {!isSmallDesktop && <FiltersInstitution />}
      <div className={styles["results"]}>
        <div className={styles["results__header"]}>
          <h1 className={styles["results--title"]}>
            Directory of U.S. Higher Education Institutions
          </h1>
          <p className={styles["results--description"]}>
            Explore our comprehensive directory of{" "}
            <b>
              <em>universities and colleges</em>
            </b>{" "}
            throughout the United States. Find the institutions that align with
            your academic interests.{" "}
          </p>
        </div>

        <div className={styles["results__body"]}>
          <div className={styles["results--controller"]}>
            <Button
              variant="outlined"
              startIcon={<BookmarkPlus size={18} />}
              onClick={() => setSaveModalOpen(true)}
              className={styles["graph-button"]}
              disabled={!Array.isArray(listData[pageToCompute]) || listData[pageToCompute].length === 0}
              sx={{
                borderRadius: "20px",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Save Search Results
            </Button>
            {isSmallDesktop && <FiltersInstitutionMobile />}
          </div>

          <FilterSelectedTags
            filters={filter}
            setFilters={updateSearchParams}
          />
          <div ref={scrollToRef}>
            <LoadingListCard
              isLoading={isLoading}
              grid={{
                gutter: 25,
                column: 1,
              }}
              componentNum={15}
              component={
                Array.isArray(listData[pageToCompute]) &&
                listData[pageToCompute].length > 0 ? (
                  <Grid container spacing="25px">
                    {listData[pageToCompute].map((e, i) => (
                      <Grid item xs={12} key={i}>
                        <InstitutionCard
                          data={e}
                          className={classNames(
                            styles["card"],
                            i % 2 === 0
                              ? styles["even-card"]
                              : styles["odd-card"],
                          )}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : showLoginSection ? (
                  <LoginRequiredSection />
                ) : (
                  <Empty style={{ alignSelf: "center" }} />
                )
              }
            />

            <InfinityPagination
              pagination={pagination}
              setPagination={updateSearchParams}
              sizeChanger={userTypeConfig?.ui_components?.pageSizeChanger}
              pageSizeOptions={[15, 30, 60, 90]}
              currentMaxPage={currentMaxPage}
              hasNextPage={hasNextPage}
              isLoading={isLoading}
              addLink
              getPageHref={getPageHref}
              numPagesPerFetch={numPagesPerFetch}
            />
          </div>
        </div>
      </div>
      <div className={styles["right-section"]}>
        {!isTablet && <CallToLogin />}
        <FeatureEntities />
      </div>
      <SaveResultsModal
        open={saveModalOpen}
        onOpenChange={setSaveModalOpen}
        entityType="institutions"
        currentItems={Array.isArray(listData[pageToCompute]) ? listData[pageToCompute] : []}
        currentFilters={filter}
        fetchItems={handleFetchTopInstitutions}
      />
    </div>
  );
};

export default InstitutionList;
